import { z } from "zod";
import { AiProposalArtifactV1Schema } from "./ai-proposal";

const isoDateTimeSchema = z.string().datetime({ offset: true });
const uuidSchema = z.string().uuid();
const decisionGroupIdSchema = z.string().trim().min(1).max(255);
const nullableMoneySchema = z.number().finite().nonnegative().nullable();

/** AI action types persisted in ai_actions. */
export const ActionTypeSchema = z.enum([
  "increase_bid",
  "decrease_bid",
  "keep_bid",
  "observe",
  "pause_keyword",
  "resume_keyword",
  "suggest_negative_keyword",
  "suggest_harvest_keyword",
  "add_keyword_tag",
]);
export type ActionType = z.infer<typeof ActionTypeSchema>;

export const RiskLevelSchema = z.enum(["low", "medium", "high", "critical"]);
export type RiskLevel = z.infer<typeof RiskLevelSchema>;

export const ConfidenceSchema = z.enum(["low", "medium", "high"]);
export type Confidence = z.infer<typeof ConfidenceSchema>;

export const ExecutionModeLogSchema = z.enum(["auto", "approval", "recommendation"]);
export type ExecutionModeLog = z.infer<typeof ExecutionModeLogSchema>;

/** Complete ai_actions lifecycle, including Chat-replaced actions. */
export const AiLogStatusSchema = z.enum([
  "proposed",
  "pending_approval",
  "approved",
  "rejected",
  "superseded",
  "executing",
  "executed",
  "failed",
  "skipped",
]);
export type AiLogStatus = z.infer<typeof AiLogStatusSchema>;

/** Complete ai_runs lifecycle, including the Mastra human-in-the-loop gate. */
export const AiRunStatusSchema = z.enum(["running", "suspended", "success", "partial", "failed"]);
export type AiRunStatus = z.infer<typeof AiRunStatusSchema>;

export const AiJobTypeSchema = z.enum(["daily_bid_optimization", "daily_search_term_mining"]);
export type AiJobType = z.infer<typeof AiJobTypeSchema>;

export const AiTriggerTypeSchema = z.enum(["cron", "manual"]);
export type AiTriggerType = z.infer<typeof AiTriggerTypeSchema>;

/** Human review state, independent from routing and Amazon execution. */
export const ApprovalReviewStatusSchema = z.enum([
  "not_required",
  "pending",
  "approved",
  "rejected",
  "expired",
  "mixed",
]);
export type ApprovalReviewStatus = z.infer<typeof ApprovalReviewStatusSchema>;

/** Proposal-level Amazon execution state derived from its supported actions. */
export const ApprovalExecutionStatusSchema = z.enum([
  "not_applicable",
  "queued",
  "executing",
  "succeeded",
  "partially_failed",
  "failed",
]);
export type ApprovalExecutionStatus = z.infer<typeof ApprovalExecutionStatusSchema>;

export const ApprovalRoutingStatusSchema = z.enum(["auto", "approval", "mixed", "recommendation"]);
export type ApprovalRoutingStatus = z.infer<typeof ApprovalRoutingStatusSchema>;

export const ApprovalActionReviewStatusSchema = z.enum([
  "not_required",
  "pending",
  "approved",
  "rejected",
  "expired",
]);
export type ApprovalActionReviewStatus = z.infer<typeof ApprovalActionReviewStatusSchema>;

export const ApprovalActionExecutionStatusSchema = z.enum([
  "not_applicable",
  "queued",
  "executing",
  "executed",
  "failed",
]);
export type ApprovalActionExecutionStatus = z.infer<typeof ApprovalActionExecutionStatusSchema>;

export const ApprovalFinalizationStatusSchema = z.enum([
  "pending",
  "processing",
  "applied",
  "failed",
  "not_required",
]);
export type ApprovalFinalizationStatus = z.infer<typeof ApprovalFinalizationStatusSchema>;

export const ApprovalProposalActionSchema = z.object({
  id: uuidSchema,
  proposalId: uuidSchema,
  runId: uuidSchema,
  campaignId: uuidSchema,
  adGroupId: uuidSchema.nullable(),
  targetId: uuidSchema.nullable(),
  targetLabel: z.string().min(1),
  actionType: ActionTypeSchema,
  oldBid: nullableMoneySchema,
  newBid: nullableMoneySchema,
  changePercentage: z.number().finite().nullable(),
  riskLevel: RiskLevelSchema,
  confidence: ConfidenceSchema,
  requiresApproval: ExecutionModeLogSchema,
  status: AiLogStatusSchema,
  route: ExecutionModeLogSchema,
  reviewStatus: ApprovalActionReviewStatusSchema,
  executionStatus: ApprovalActionExecutionStatusSchema,
  amazonRequestId: z.string().min(1).nullable(),
  amazonError: z.string().min(1).nullable(),
  createdAt: isoDateTimeSchema,
  updatedAt: isoDateTimeSchema,
  executedAt: isoDateTimeSchema.nullable(),
});
export type ApprovalProposalAction = z.infer<typeof ApprovalProposalActionSchema>;

export const ApprovalProposalListParamsSchema = z
  .object({
    reviewStatus: ApprovalReviewStatusSchema.optional(),
    executionStatus: ApprovalExecutionStatusSchema.optional(),
    riskLevel: RiskLevelSchema.optional(),
    actionType: ActionTypeSchema.optional(),
    campaignId: uuidSchema.optional(),
    managedGroupId: uuidSchema.optional(),
    createdAfter: isoDateTimeSchema.optional(),
    createdBefore: isoDateTimeSchema.optional(),
    /** ADP-127: 逗号分隔的多店范围;省略则回退 X-Shop-Id 单店语义。 */
    shopIds: z.string().min(1).optional(),
    page: z.coerce.number().int().positive().default(1),
    pageSize: z.coerce.number().int().positive().max(100).default(20),
    sortBy: z.enum(["createdAt", "riskLevel", "expiresAt"]).default("createdAt"),
    sortOrder: z.enum(["asc", "desc"]).default("desc"),
  })
  .refine(
    ({ createdAfter, createdBefore }) =>
      createdAfter === undefined ||
      createdBefore === undefined ||
      Date.parse(createdAfter) <= Date.parse(createdBefore),
    { message: "createdAfter must not be later than createdBefore", path: ["createdAfter"] },
  );
export type ApprovalProposalListParams = z.infer<typeof ApprovalProposalListParamsSchema>;

const countSchema = z.number().int().nonnegative();

export const ApprovalProposalCountsSchema = z.object({
  routes: z.object({
    auto: countSchema,
    approval: countSchema,
    recommendation: countSchema,
  }),
  reviews: z.object({
    notRequired: countSchema,
    pending: countSchema,
    approved: countSchema,
    rejected: countSchema,
    expired: countSchema,
  }),
  executions: z.object({
    notApplicable: countSchema,
    queued: countSchema,
    executing: countSchema,
    executed: countSchema,
    failed: countSchema,
  }),
});
export type ApprovalProposalCounts = z.infer<typeof ApprovalProposalCountsSchema>;

export const ApprovalProposalListItemSchema = z
  .object({
    id: uuidSchema,
    runId: uuidSchema,
    // ADP-127: 多店范围下列表跨店铺,标注来源店铺。
    shopId: uuidSchema.optional(),
    shopName: z.string().nullable().optional(),
    /** Immutable group identity captured by the Run when this Proposal was created. */
    managedGroupId: uuidSchema.nullable().default(null),
    managedGroupName: z.string().nullable().default(null),
    campaignId: uuidSchema,
    campaignName: z.string().min(1),
    actions: z.array(ApprovalProposalActionSchema),
    actionCount: z.number().int().nonnegative(),
    counts: ApprovalProposalCountsSchema,
    highestRisk: RiskLevelSchema.nullable(),
    routingStatus: ApprovalRoutingStatusSchema,
    reviewStatus: ApprovalReviewStatusSchema,
    executionStatus: ApprovalExecutionStatusSchema,
    finalizationStatus: ApprovalFinalizationStatusSchema,
    createdAt: isoDateTimeSchema,
    updatedAt: isoDateTimeSchema,
    expiresAt: isoDateTimeSchema.nullable(),
  })
  .refine(({ actionCount, actions }) => actionCount === actions.length, {
    message: "actionCount must equal the number of current actions",
    path: ["actionCount"],
  })
  .superRefine(({ id, runId, campaignId, actions, counts }, context) => {
    if (
      actions.some(
        (action) =>
          action.proposalId !== id || action.runId !== runId || action.campaignId !== campaignId,
      )
    ) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Every Action must belong to this Proposal, Run, and Campaign",
        path: ["actions"],
      });
    }
    const expected = {
      routes: {
        auto: actions.filter(({ route }) => route === "auto").length,
        approval: actions.filter(({ route }) => route === "approval").length,
        recommendation: actions.filter(({ route }) => route === "recommendation").length,
      },
      reviews: {
        notRequired: actions.filter(({ reviewStatus }) => reviewStatus === "not_required").length,
        pending: actions.filter(({ reviewStatus }) => reviewStatus === "pending").length,
        approved: actions.filter(({ reviewStatus }) => reviewStatus === "approved").length,
        rejected: actions.filter(({ reviewStatus }) => reviewStatus === "rejected").length,
        expired: actions.filter(({ reviewStatus }) => reviewStatus === "expired").length,
      },
      executions: {
        notApplicable: actions.filter(({ executionStatus }) => executionStatus === "not_applicable")
          .length,
        queued: actions.filter(({ executionStatus }) => executionStatus === "queued").length,
        executing: actions.filter(({ executionStatus }) => executionStatus === "executing").length,
        executed: actions.filter(({ executionStatus }) => executionStatus === "executed").length,
        failed: actions.filter(({ executionStatus }) => executionStatus === "failed").length,
      },
    };

    if (JSON.stringify(counts) !== JSON.stringify(expected)) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Proposal counts must match the current Actions",
        path: ["counts"],
      });
    }
  });
export type ApprovalProposalListItem = z.infer<typeof ApprovalProposalListItemSchema>;

export const ApprovalProposalSummarySchema = z.object({
  pending: z.number().int().nonnegative(),
  approvedToday: z.number().int().nonnegative(),
  rejectedToday: z.number().int().nonnegative(),
  expiringSoon: z.number().int().nonnegative(),
});
export type ApprovalProposalSummary = z.infer<typeof ApprovalProposalSummarySchema>;

export const ApprovalPaginationSchema = z.object({
  page: z.number().int().positive(),
  pageSize: z.number().int().positive().max(100),
  total: z.number().int().nonnegative(),
  hasMore: z.boolean(),
});
export type ApprovalPagination = z.infer<typeof ApprovalPaginationSchema>;

export const ApprovalProposalListResponseSchema = z.object({
  success: z.literal(true),
  data: z.array(ApprovalProposalListItemSchema),
  pagination: ApprovalPaginationSchema,
  summary: ApprovalProposalSummarySchema,
});
export type ApprovalProposalListResponse = z.infer<typeof ApprovalProposalListResponseSchema>;

export const ApprovalMetricWindowSchema = z.object({
  impressions: z.number().int().nonnegative(),
  clicks: z.number().int().nonnegative(),
  spend: z.number().finite().nonnegative(),
  sales: z.number().finite().nonnegative(),
  orders: z.number().int().nonnegative(),
  ctr: z.number().finite().nonnegative(),
  cvr: z.number().finite().nonnegative(),
  acos: z.number().finite().nonnegative().nullable(),
  roas: z.number().finite().nonnegative().nullable(),
  cpc: z.number().finite().nonnegative().nullable(),
});
export type ApprovalMetricWindow = z.infer<typeof ApprovalMetricWindowSchema>;

export const ApprovalReviewMetricsSchema = z.object({
  measuredAt: isoDateTimeSchema,
  windows: z.object({
    "7d": ApprovalMetricWindowSchema,
    "14d": ApprovalMetricWindowSchema,
    "30d": ApprovalMetricWindowSchema,
  }),
  targetAcos: z.number().finite().nonnegative().nullable(),
});
export type ApprovalReviewMetrics = z.infer<typeof ApprovalReviewMetricsSchema>;

export const ApprovalDecisionEventPhaseSchema = z.enum(["requested", "applied", "failed"]);
export type ApprovalDecisionEventPhase = z.infer<typeof ApprovalDecisionEventPhaseSchema>;

export const ApprovalDecisionSchema = z.enum([
  "approve",
  "reject",
  "modify_approve",
  "chat",
  "expire",
]);
export type ApprovalDecision = z.infer<typeof ApprovalDecisionSchema>;

/** Audit-only decision set. `finalize` is never accepted from a public request body. */
export const ApprovalAuditDecisionSchema = z.enum([
  "approve",
  "reject",
  "modify_approve",
  "chat",
  "expire",
  "finalize",
]);
export type ApprovalAuditDecision = z.infer<typeof ApprovalAuditDecisionSchema>;

export const ApprovalDecisionHistoryItemSchema = z.object({
  id: uuidSchema,
  decisionGroupId: decisionGroupIdSchema,
  actionId: uuidSchema,
  phase: ApprovalDecisionEventPhaseSchema,
  decision: ApprovalAuditDecisionSchema,
  actorUserId: uuidSchema.nullable(),
  reasonCode: z.string().min(1).nullable(),
  note: z.string().min(1).nullable(),
  previousBid: nullableMoneySchema,
  decidedBid: nullableMoneySchema,
  failureReason: z.string().min(1).nullable(),
  createdAt: isoDateTimeSchema,
});
export type ApprovalDecisionHistoryItem = z.infer<typeof ApprovalDecisionHistoryItemSchema>;

export const ApprovalProposalTargetSchema = z.object({
  id: uuidSchema,
  adGroupId: uuidSchema.nullable(),
  targetType: z.enum(["keyword", "auto", "product", "product_category", "theme"]),
  label: z.string().min(1),
  keywordText: z.string().min(1).nullable(),
  keywordMatchType: z.enum(["exact", "phrase", "broad"]).nullable(),
  expressionType: z.enum(["close_match", "loose_match", "substitutes", "complements"]).nullable(),
  asin: z.string().min(1).nullable(),
  details: z.record(z.string(), z.unknown()).nullable(),
  bid: nullableMoneySchema,
  tags: z.array(z.object({ tag: z.string().min(1), source: z.enum(["user", "ai"]) })),
});
export type ApprovalProposalTarget = z.infer<typeof ApprovalProposalTargetSchema>;

export const ApprovalProposalDetailSchema = ApprovalProposalListItemSchema.and(
  z.object({
    run: z.object({
      status: AiRunStatusSchema,
      jobType: AiJobTypeSchema,
      triggerType: AiTriggerTypeSchema,
      startedAt: isoDateTimeSchema,
      finishedAt: isoDateTimeSchema.nullable(),
    }),
    adGroups: z.array(z.object({ id: uuidSchema, name: z.string().min(1) })),
    targets: z.array(ApprovalProposalTargetSchema),
    strategyContext: z.object({
      proposalRound: z.number().int().positive(),
      policySnapshotId: uuidSchema,
    }),
    artifact: AiProposalArtifactV1Schema,
    reviewMetrics: ApprovalReviewMetricsSchema,
    decisionHistory: z.array(ApprovalDecisionHistoryItemSchema),
  }),
);
export type ApprovalProposalDetail = z.infer<typeof ApprovalProposalDetailSchema>;

export const ApprovalProposalDetailResponseSchema = z.object({
  success: z.literal(true),
  data: ApprovalProposalDetailSchema,
});
export type ApprovalProposalDetailResponse = z.infer<typeof ApprovalProposalDetailResponseSchema>;

export const ApprovalRejectionReasonSchema = z.enum([
  "strategic_keyword",
  "core_keyword_protection",
  "insufficient_data",
  "external_promotion",
  "inventory_reason",
  "wrong_interpretation",
  "manual_override",
  "other",
]);
export type ApprovalRejectionReason = z.infer<typeof ApprovalRejectionReasonSchema>;

export const ApprovalIdempotencyHeaderSchema = z.object({
  "idempotency-key": z.string().trim().min(1).max(255),
});
export type ApprovalIdempotencyHeader = z.infer<typeof ApprovalIdempotencyHeaderSchema>;

export const ApprovalApproveRequestSchema = z.object({}).strict();
export type ApprovalApproveRequest = z.infer<typeof ApprovalApproveRequestSchema>;

export const ApprovalRejectRequestSchema = z
  .object({
    reasonCode: ApprovalRejectionReasonSchema.optional(),
    note: z.string().trim().min(1).max(2000).optional(),
  })
  .strict();
export type ApprovalRejectRequest = z.infer<typeof ApprovalRejectRequestSchema>;

/** Amazon Ads system bid bounds shared by proposal validation and risk controls. */
export const AMAZON_BID_MIN_USD = 0.1;
export const AMAZON_BID_MAX_USD = 10;

export const ApprovalBidModificationSchema = z.object({
  actionId: uuidSchema,
  // Target type is resolved at the trusted service boundary. Keyword keeps
  // these hard bounds; Auto treats them as Critical warnings, not blockers.
  modifiedBid: z.number().finite().positive(),
});
export type ApprovalBidModification = z.infer<typeof ApprovalBidModificationSchema>;

export const ApprovalSubmitActionOutcomeSchema = z.discriminatedUnion("decision", [
  z
    .object({
      actionId: uuidSchema,
      decision: z.literal("approve"),
      note: z.string().trim().min(1).max(2000).optional(),
    })
    .strict(),
  z
    .object({
      actionId: uuidSchema,
      decision: z.literal("reject"),
      reasonCode: ApprovalRejectionReasonSchema.optional(),
      note: z.string().trim().min(1).max(2000).optional(),
    })
    .strict(),
  z
    .object({
      actionId: uuidSchema,
      decision: z.literal("modify_approve"),
      modifiedBid: z.number().finite().positive(),
      note: z.string().trim().min(1).max(2000).optional(),
    })
    .strict(),
]);
export type ApprovalSubmitActionOutcome = z.infer<typeof ApprovalSubmitActionOutcomeSchema>;

export const ApprovalSubmitRequestSchema = z
  .object({
    actions: z.array(ApprovalSubmitActionOutcomeSchema).min(1),
  })
  .strict()
  .superRefine(({ actions }, context) => {
    const seen = new Set<string>();
    actions.forEach((outcome, index) => {
      const { actionId } = outcome;
      if (seen.has(actionId)) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Each Action must have exactly one submitted outcome",
          path: ["actions", index, "actionId"],
        });
      }
      seen.add(actionId);
    });
  });
export type ApprovalSubmitRequest = z.infer<typeof ApprovalSubmitRequestSchema>;

export const BulkApprovalProposalSchema = z
  .object({
    proposalId: uuidSchema,
    shopId: uuidSchema,
    /** Exact pending Action set shown to the reviewer; binds the command to that revision. */
    actionIds: z.array(uuidSchema).min(1),
  })
  .strict()
  .superRefine(({ actionIds }, context) => {
    const seen = new Set<string>();
    actionIds.forEach((actionId, index) => {
      if (seen.has(actionId)) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Each Action may appear only once per Proposal",
          path: ["actionIds", index],
        });
      }
      seen.add(actionId);
    });
  });
export type BulkApprovalProposal = z.infer<typeof BulkApprovalProposalSchema>;

export const BulkApprovalRequestSchema = z
  .object({
    proposals: z.array(BulkApprovalProposalSchema).min(1).max(20),
  })
  .strict()
  .superRefine(({ proposals }, context) => {
    const seen = new Set<string>();
    proposals.forEach(({ proposalId }, index) => {
      if (seen.has(proposalId)) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Each Proposal may appear only once",
          path: ["proposals", index, "proposalId"],
        });
      }
      seen.add(proposalId);
    });
  });
export type BulkApprovalRequest = z.infer<typeof BulkApprovalRequestSchema>;

export const BulkApprovalResultSchema = z.object({
  status: z.literal("processing"),
  proposals: z.array(
    z.object({
      proposalId: uuidSchema,
      runId: uuidSchema,
      actionIds: z.array(uuidSchema).min(1),
    }),
  ),
});
export type BulkApprovalResult = z.infer<typeof BulkApprovalResultSchema>;

export const BulkApprovalResponseSchema = z.object({
  success: z.literal(true),
  data: BulkApprovalResultSchema,
});
export type BulkApprovalResponse = z.infer<typeof BulkApprovalResponseSchema>;

export const ApprovalModifyApproveRequestSchema = z
  .object({
    modifications: z.array(ApprovalBidModificationSchema).min(1),
    note: z.string().trim().min(1).max(2000).optional(),
  })
  .superRefine(({ modifications }, context) => {
    const seen = new Set<string>();
    modifications.forEach(({ actionId }, index) => {
      if (seen.has(actionId)) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Each action may be modified only once",
          path: ["modifications", index, "actionId"],
        });
      }
      seen.add(actionId);
    });
  });
export type ApprovalModifyApproveRequest = z.infer<typeof ApprovalModifyApproveRequestSchema>;

export const ApprovalChatRequestSchema = z.object({
  message: z.string().trim().min(1).max(2000),
});
export type ApprovalChatRequest = z.infer<typeof ApprovalChatRequestSchema>;

export const ApprovalDecisionResultStatusSchema = z.enum([
  "processing",
  "partial",
  "approved",
  "rejected",
  "revised",
  "expired",
]);
export type ApprovalDecisionResultStatus = z.infer<typeof ApprovalDecisionResultStatusSchema>;

export const ApprovalDecisionResultSchema = z.object({
  proposalId: uuidSchema.optional(),
  runId: uuidSchema,
  decisionGroupId: decisionGroupIdSchema,
  status: ApprovalDecisionResultStatusSchema,
  actionIds: z.array(uuidSchema),
  replayUrl: z.string().min(1).nullable(),
});
export type ApprovalDecisionResult = z.infer<typeof ApprovalDecisionResultSchema>;

export const ApprovalDecisionResponseSchema = z.object({
  success: z.literal(true),
  data: ApprovalDecisionResultSchema,
});
export type ApprovalDecisionResponse = z.infer<typeof ApprovalDecisionResponseSchema>;
